import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useLanguage } from './LanguageContext';
import { notificationsAPI, contactChatsAPI } from '../services/api';
import { parseNotificationsResponse } from '../utils/customerNotifications';
import { getWsUrl } from '../utils/config';
import { toast } from 'react-toastify';

const NotificationContext = createContext(null);

/** How often to poll unread count + preview while authenticated (silent = no loading flicker). */
const NOTIFICATION_POLL_MS = 3000;

/**
 * Shared notification preview + unread count for the Layout bell and child pages.
 * Call {@link refresh} after mark-read / mark-all so the badge updates without a full page reload.
 *
 * @param {boolean} [silent=false] — when true, skip loading spinner (e.g. after quick mutations).
 */
export function NotificationProvider({ children }) {
  const { token } = useAuth();
  const { t, locale } = useLanguage();
  const [previewItems, setPreviewItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const openChat = useCallback((relatedEntityId, contactId = null) => {
    window.dispatchEvent(new CustomEvent('open-chat', { detail: { relatedEntityId, contactId } }));
    const query = new URLSearchParams();
    if (relatedEntityId) query.append('chatId', relatedEntityId);
    if (contactId) query.append('contactId', contactId);
    navigate(`/chat${query.toString() ? `?${query.toString()}` : ''}`);
  }, [navigate]);

  const showNotificationToast = useCallback((data) => {
    const title = data.title || data.notification?.title || data.payload?.title || 'Notification';
    const body = data.body || data.message || data.notification?.body || data.payload?.body || data.payload?.message || '';
    
    toast(
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '2px 0',
        fontFamily: '"Outfit", "Inter", sans-serif',
      }}>
        <div style={{
          width: '38px',
          height: '38px',
          borderRadius: '50%',
          backgroundColor: '#E6F2F5',
          border: '1.5px solid #4A7C8E',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: '12px',
          flexShrink: 0,
          boxShadow: '0 2px 8px rgba(74, 124, 142, 0.15)',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4A7C8E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: '700',
            fontSize: '0.9rem',
            color: '#1E3A45',
            marginBottom: '2px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {title}
          </div>
          <div style={{
            fontSize: '0.775rem',
            color: '#6B7280',
            lineHeight: '1.3',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {body}
          </div>
        </div>
      </div>,
      {
        position: 'top-right',
        autoClose: 5000,
        icon: false,
        progressStyle: {
          background: '#4A7C8E',
        },
        style: {
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(30, 58, 69, 0.1), 0 8px 10px -6px rgba(30, 58, 69, 0.1)',
          border: '1px solid #E5E7EB',
          padding: '12px',
          '--toastify-color-progress-light': '#4A7C8E',
        }
      }
    );
  }, []);

  const showChatToast = useCallback((data) => {
    const name = data.title || data.notification?.title || data.payload?.title || '';
    const body = data.body || data.message || data.notification?.body || data.payload?.body || data.payload?.message || '';
    const isGenericTitle = !name || name.trim().toLowerCase() === 'new message';
    const toastId = data.relatedEntityId || `chat-${Date.now()}`;
    
    toast(
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '2px 0',
        fontFamily: '"Outfit", "Inter", sans-serif',
      }}>
        <div style={{
          width: '38px',
          height: '38px',
          borderRadius: '50%',
          backgroundColor: '#FFF5EF',
          border: '1.5px solid #FF8C42',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: '12px',
          flexShrink: 0,
          boxShadow: '0 2px 8px rgba(255, 140, 66, 0.15)',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FF8C42" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '0.75rem',
            fontWeight: '700',
            color: '#FF8C42',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '2px'
          }}>
            New Message
          </div>
          {!isGenericTitle && (
            <div style={{
              fontWeight: '700',
              fontSize: '0.95rem',
              color: '#1E3A45',
              marginBottom: '2px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {name}
            </div>
          )}
          <div style={{
            fontSize: '0.8rem',
            color: '#6B7280',
            lineHeight: '1.3',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {body}
          </div>
        </div>
      </div>,
      {
        toastId,
        position: 'top-center',
        autoClose: 5000,
        icon: false,
        progressStyle: {
          background: '#FF8C42',
        },
        onClick: () => {
          if (data.relatedEntityId) {
            contactChatsAPI.markAsRead(data.relatedEntityId, null)
              .then(() => refresh(true))
              .catch((err) => console.error('Failed to mark chat as read:', err));
          }
          openChat(data.relatedEntityId, data.contactId || data.payload?.contactId);
        },
        style: {
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(30, 58, 69, 0.1), 0 8px 10px -6px rgba(30, 58, 69, 0.1)',
          border: '1px solid #E5E7EB',
          padding: '12px',
          '--toastify-color-progress-light': '#FF8C42',
        }
      }
    );
  }, [openChat]);

  const refresh = useCallback(
    async (silent = false) => {
      if (!token) {
        setPreviewItems([]);
        setUnreadCount(0);
        setChatUnreadCount(0);
        setError('');
        return;
      }
      if (!silent) setLoading(true);
      setError('');
      try {
        const notifPromise = notificationsAPI.getList(1, 20, locale)
          .then((res) => {
            const { unreadCount: nextUnread, items } = parseNotificationsResponse(res);
            setUnreadCount(nextUnread);
            setPreviewItems(items);
          })
          .catch((err) => {
            console.error('Failed to load notifications:', err);
            setError(t('layout.notifications.loadError'));
            setPreviewItems([]);
          });

        const chatPromise = contactChatsAPI.getConversations()
          .then((res) => {
            const root = res?.data;
            const payload = root?.payload ?? root?.data ?? root ?? {};
            const count = Number(payload.totalUnread) || 0;
            setChatUnreadCount(count);
          })
          .catch((err) => {
            console.error('Failed to fetch unread chat count:', err);
            setChatUnreadCount(0);
          });

        await Promise.all([notifPromise, chatPromise]);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [token, t, locale]
  );

  useEffect(() => {
    refresh(false);
  }, [refresh]);

  useEffect(() => {
    if (!token) return undefined;

    let socket = null;
    let reconnectTimeout = null;
    let isDisposed = false;

    const connect = () => {
      if (isDisposed || !token) return;

      try {
        const wsUrl = getWsUrl();
        if (!wsUrl) return;

        const wsEndpoint = `${wsUrl}?token=${encodeURIComponent(token)}`;
        socket = new WebSocket(wsEndpoint);

        socket.addEventListener('open', () => {
          console.log('[NotificationWS] WebSocket connected');
          refresh(true);
        });

        socket.addEventListener('message', (browserEvent) => {
          console.log('[NotificationWS] Message received:', browserEvent.data);
          let data;
          try {
            data = JSON.parse(browserEvent.data);
          } catch (e) {
            console.error('Failed to parse WebSocket message data as JSON', e);
            refresh(true);
            return;
          }

          switch (data.type) {
            case 'notification':
              showNotificationToast(data);
              refresh(true);
              break;

            case 'chat':
              refresh(true);
              break;

            case 'refresh':
              refresh(true);
              break;

            default:
              if (data.title || data.body || data.message) {
                showNotificationToast(data);
                refresh(true);
              } else {
                console.warn('Unknown WS message type', data);
              }
          }

          window.dispatchEvent(new CustomEvent('notification-received', { detail: data }));
        });

        socket.addEventListener('close', (event) => {
          console.log(`[NotificationWS] WebSocket closed: code=${event.code}, reason=${event.reason}`);
          socket = null;
          if (!isDisposed) {
            console.log('[NotificationWS] Reconnecting in 3000ms...');
            reconnectTimeout = setTimeout(() => {
              connect();
            }, 3000);
          }
        });

        socket.addEventListener('error', (e) => {
          console.error('[NotificationWS] WebSocket error', e);
        });
      } catch (err) {
        console.error('[NotificationWS] Connection error:', err);
      }
    };

    connect();

    return () => {
      isDisposed = true;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (socket) {
        socket.close();
      }
    };
  }, [token, refresh, showNotificationToast, showChatToast, openChat]);

  const value = useMemo(
    () => ({
      previewItems,
      unreadCount,
      chatUnreadCount,
      loading,
      error,
      refresh,
    }),
    [previewItems, unreadCount, chatUnreadCount, loading, error, refresh]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return ctx;
}
